import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FaUsers,
  FaUserClock,
  FaMoneyBillWave,
  FaSyncAlt,
  FaUserPlus,
  FaIdBadge,
  FaClipboardList,
  FaUmbrellaBeach,
  FaBuilding,
  FaFingerprint,
  FaBook,
  FaTasks,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const ALL_DEPT = "ALL";

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const QUICK_LINKS = [
  {
    to: "/AdminMain/StaffAttendanceDash",
    title: "Staff Attendance",
    icon: FaUserClock,
    accent: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  },
  {
    to: "/AdminMain/StaffBiometricId",
    title: "Biometric ID",
    icon: FaFingerprint,
    accent: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  {
    to: "/AdminMain/LeaveManagement?type=staff",
    title: "Staff Leave",
    icon: FaUmbrellaBeach,
    accent: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  },
  {
    to: "/AdminMain/DepartmentSettings",
    title: "Departments",
    icon: FaBuilding,
    accent: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  },
  {
    to: "/AdminMain/StaffSubject",
    title: "Staff Subject",
    icon: FaBook,
    accent: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
  },
  //Created By Cursor
  {
    to: "/AdminMain/ActivityManagement",
    title: "Activity Management",
    icon: FaTasks,
    accent: "linear-gradient(135deg, #0288D1 0%, #03A9F4 100%)",
  },
  {
    to: "/AdminMain/StaffRegistration",
    title: "Register Staff",
    icon: FaClipboardList,
    accent: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
  },
];

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.api_remark || err?.message || fallback;
}

function parseYearMonthKey(key) {
  if (!key) return null;
  const [yearStr, monthStr] = String(key).split("-");
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

function pickMonthInSession(session, preferredKey) {
  const options = buildMonthOptions(session);
  if (options.length === 0) return "";
  if (preferredKey && options.some((o) => o.value === preferredKey)) return preferredKey;
  return options[0].value;
}

function formatYearMonth(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length >= 2) {
    const y = parts[0];
    const m = parseInt(parts[1], 10);
    if (m >= 1 && m <= 12) return `${MONTH_SHORT[m - 1]} ${y}`;
  }
  return String(value);
}

function formatINR(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function numOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function monthKeyFromDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function buildMonthOptions(session) {
  if (!session?.start_date || !session?.end_date) return [];
  const startDate = new Date(session.start_date);
  const endDate = new Date(session.end_date);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return [];
  const months = [];
  const cur = new Date(startDate);
  while (cur <= endDate) {
    const monthKey = monthKeyFromDate(cur);
    months.push({
      value: monthKey,
      label: `${cur.toLocaleString("default", { month: "long" })} ${cur.getFullYear()}`,
      key: monthKey,
    });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

function resolveMonthForSession(session, refDate = new Date()) {
  if (!session?.start_date || !session?.end_date) return "";
  const startDate = new Date(session.start_date);
  const endDate = new Date(session.end_date);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return "";

  const y = refDate.getFullYear();
  const m = refDate.getMonth() + 1;
  const startY = startDate.getFullYear();
  const endY = endDate.getFullYear();
  const startM = startDate.getMonth() + 1;
  const endM = endDate.getMonth() + 1;
  const inRange =
    startY <= y && endY >= y && m >= startM && m <= endM;

  return inRange ? monthKeyFromDate(refDate) : `${startY}-${String(startM).padStart(2, "0")}`;
}

function pickDefaultSession(sessions, refDate = new Date()) {
  if (!sessions.length) return null;
  const year = String(refDate.getFullYear());
  return (
    sessions.find((s) => s.status === "Active" || s.session_name?.includes(year)) ||
    sessions[0]
  );
}

function computePayrollSummary(staffList) {
  let monthTotal = 0;
  let totalPaid = 0;
  let totalBalance = 0;
  let processedPayments = 0;
  let pendingPayments = 0;
  let present = 0;
  let absent = 0;
  let leave = 0;
  let late = 0;

  for (const s of staffList) {
    monthTotal += numOrZero(s.net_salary);
    totalPaid += numOrZero(s.total_paid);
    totalBalance += numOrZero(s.balance_due);
    present += numOrZero(s.staff_present);
    absent += numOrZero(s.staff_absent);
    leave += numOrZero(s.staff_leave);
    late += numOrZero(s.late_day);
    const status = String(s.status || "").trim();
    if (status === "Paid") processedPayments += 1;
    else if (status === "Pending" || status === "Processing") pendingPayments += 1;
  }

  return {
    totalStaff: staffList.length,
    monthTotal,
    totalPaid,
    totalBalance,
    processedPayments,
    pendingPayments,
    attendance: { present, absent, leave, late },
  };
}

function staffInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-IN");
}

function SummaryCells({ cells }) {
  return (
    <div className="pp-summary-grid">
      {cells.map(({ label, value, tone }) => (
        <div key={label} className={`pp-summary-cell pp-summary-cell--${tone}`}>
          <span className="pp-summary-cell__label">{label}</span>
          <span className="pp-summary-cell__value">{value}</span>
        </div>
      ))}
    </div>
  );
}

function KpiChip({ label, value, icon, tone = "blue", loading = false }) {
  return (
    <article
      className={`smhub-metric smhub-metric--${tone}${loading ? " is-loading" : ""}`}
    >
      <span className="smhub-metric__icon" aria-hidden>
        {icon}
      </span>
      <div className="smhub-metric__body">
        <span className="smhub-metric__label">{label}</span>
        <strong className="smhub-metric__value">{value}</strong>
      </div>
    </article>
  );
}

function DashboardTile({ to, title, icon: Icon, accent }) {
  return (
    <Link to={to} className="sm-quick-link smhub-quick-link" aria-label={title}>
      <span className="sm-quick-link__chevron" aria-hidden>
        ›
      </span>
      <span className="sm-quick-link__icon" style={{ background: accent }}>
        <Icon />
      </span>
      <span className="sm-quick-link__label">{title}</span>
    </Link>
  );
}

const StaffAvatar = React.memo(function StaffAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="sm-staff-card__avatar">
      {showImg ? (
        <img
          src={imagePath}
          alt=""
          className="sm-staff-card__img"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="sm-staff-card__initials">{staffInitials(name)}</span>
      )}
    </div>
  );
});

const StaffCard = React.memo(function StaffCard({ staff }) {
  const staffId = staff.staff_id ?? staff.id;
  if (staffId == null || staffId === "") {
    return null;
  }
  const profilePath = `/AdminMain/StaffProfile/${encodeURIComponent(String(staffId))}`;
  const biometricId = String(staff.biometric_id ?? "").trim();
  const hasBiometric = Boolean(biometricId);

  return (
    <Link to={profilePath} className="sm-staff-card smhub-staff-card">
      <StaffAvatar name={staff.staff_name} imagePath={staff.staff_image_path} />
      <div className="sm-staff-card__body">
        <div className="sm-staff-card__head">
          <h3 className="sm-staff-card__name">{staff.staff_name || "Unnamed"}</h3>
          {staff.staff_role ? (
            <span className="sm-staff-card__role smhub-staff-card__role">
              {staff.staff_role}
            </span>
          ) : null}
        </div>
        <dl className="sm-staff-card__meta">
          <div className="sm-staff-card__row">
            <dt>Department</dt>
            <dd>{staff.department_name || "—"}</dd>
          </div>
          <div
            className={`sm-staff-card__row sm-staff-card__row--biometric${
              hasBiometric ? "" : " sm-staff-card__row--biometric-missing"
            }`}
          >
            <dt>
              <FaFingerprint className="sm-staff-card__bio-icon" aria-hidden />
              Biometric
            </dt>
            <dd>
              <span
                className={`sm-staff-card__bio-value${
                  hasBiometric
                    ? " sm-staff-card__bio-value--set"
                    : " sm-staff-card__bio-value--missing"
                }`}
              >
                {hasBiometric ? biometricId : "Not set"}
              </span>
            </dd>
          </div>
        </dl>
      </div>
      <span className="smhub-staff-card__go" aria-hidden>
        ›
      </span>
    </Link>
  );
});

/**
 * Staff hub: directory, session/month overview, and quick links.
 */
function StaffManagement() {
  const loggedIn = localStorage.getItem("token") != null;

  const [bootLoading, setBootLoading] = useState(true);
  const [staffDirectory, setStaffDirectory] = useState([]);
  const [mSessionList, setMSessionList] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [payrollRows, setPayrollRows] = useState([]);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deptFilter, setDeptFilter] = useState(ALL_DEPT);

  const sessionReqRef = useRef(0);
  const payrollReqRef = useRef(0);

  const selectedSession = useMemo(
    () => mSessionList.find((s) => String(s.session_id) === String(selectedSessionId)),
    [mSessionList, selectedSessionId]
  );

  const monthOptions = useMemo(
    () => buildMonthOptions(selectedSession),
    [selectedSession]
  );

  const dashboard = useMemo(
    () => computePayrollSummary(payrollRows),
    [payrollRows]
  );

  const metrics = useMemo(
    () => [
      {
        label: "School Staff",
        value: staffDirectory.length,
        icon: <FaUsers />,
        tone: "blue",
      },
      {
        label: "On Payroll",
        value: dashboard.totalStaff,
        icon: <FaIdBadge />,
        tone: "sky",
      },
      {
        label: "Present (Σ)",
        value: Math.round(dashboard.attendance.present),
        icon: <FaUserClock />,
        tone: "mint",
      },
      {
        label: "Net Payroll",
        value: formatINR(dashboard.monthTotal),
        icon: <FaMoneyBillWave />,
        tone: "sand",
      },
    ],
    [staffDirectory.length, dashboard]
  );

  const attendanceCells = useMemo(
    () => [
      { label: "Present", value: formatCount(dashboard.attendance.present), tone: "success" },
      { label: "Absent", value: formatCount(dashboard.attendance.absent), tone: "danger" },
      { label: "Leave", value: formatCount(dashboard.attendance.leave), tone: "warn" },
      { label: "Late", value: formatCount(dashboard.attendance.late), tone: "muted" },
    ],
    [
      dashboard.attendance.present,
      dashboard.attendance.absent,
      dashboard.attendance.leave,
      dashboard.attendance.late,
    ]
  );

  const payrollCells = useMemo(
    () => [
      { label: "Paid out", value: formatINR(dashboard.totalPaid), tone: "success" },
      { label: "Balance due", value: formatINR(dashboard.totalBalance), tone: "danger" },
      { label: "Paid records", value: formatCount(dashboard.processedPayments), tone: "muted" },
      { label: "Pending", value: formatCount(dashboard.pendingPayments), tone: "warn" },
    ],
    [dashboard]
  );

  const periodLabel = selectedMonth ? formatYearMonth(selectedMonth) : null;

  //Created By Cursor — unique departments for directory filter
  const departmentOptions = useMemo(() => {
    const map = new Map();
    for (const staff of staffDirectory) {
      const name = String(staff.department_name || "").trim();
      if (!name) continue;
      map.set(name, (map.get(name) || 0) + 1);
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [staffDirectory]);

  //Created By Cursor — filtered staff directory
  const filteredStaff = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return staffDirectory.filter((staff) => {
      const dept = String(staff.department_name || "").trim();
      if (deptFilter !== ALL_DEPT && dept !== deptFilter) return false;
      if (!q) return true;
      const hay = [
        staff.staff_name,
        staff.staff_role,
        staff.department_name,
        staff.biometric_id,
        staff.staff_id,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [staffDirectory, searchQuery, deptFilter]);

  const biometricMissingCount = useMemo(
    () =>
      staffDirectory.filter(
        (s) => !String(s.biometric_id ?? "").trim()
      ).length,
    [staffDirectory]
  );

  const loadStaffDirectory = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setStaffDirectory([]);
      myToast.notify_warning("School code is missing. Cannot load staff.");
      return false;
    }
    const staffListParams = { school_code: schoolCode };
    const staffListUrl = `${API_BASE()}getStaff.php`;
    console.log("[StaffManagement] getStaff.php", {
      url: staffListUrl,
      params: staffListParams,
    });
    try {
      const res = await axios.get(staffListUrl, { params: staffListParams });
      const data = res.data;
      console.log("[StaffManagement] getStaff.php response", {
        api_status: data?.api_status,
        count: Array.isArray(data?.staff) ? data.staff.length : 0,
        api_remark: data?.api_remark,
      });
      if (data?.api_status === "OK") {
        setStaffDirectory(Array.isArray(data.staff) ? data.staff : []);
        return true;
      }
      setStaffDirectory([]);
      myToast.notify_warning(data?.api_remark || "Could not load staff directory.");
      return false;
    } catch (e) {
      console.error("[StaffManagement] getStaff.php error", {
        params: staffListParams,
        message: apiErrorMessage(e, "Network error loading staff."),
      });
      setStaffDirectory([]);
      myToast.notify_warning(apiErrorMessage(e, "Network error loading staff."));
      return false;
    }
  }, []);

  const fetchSessionList = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setMSessionList([]);
      myToast.notify_warning("School code is missing. Cannot load sessions.");
      return false;
    }
    const reqId = ++sessionReqRef.current;
    try {
      const response = await axios.get(`${API_BASE()}getSessionList.php`, {
        params: { school_code: schoolCode },
      });
      if (reqId !== sessionReqRef.current) return false;
      if (response.data?.api_status !== "OK") {
        setMSessionList([]);
        myToast.notify_warning(
          response.data?.api_remark || "Could not load academic sessions."
        );
        return false;
      }

      const sessions = Array.isArray(response.data.data) ? response.data.data : [];
      setMSessionList(sessions);
      const chosen = pickDefaultSession(sessions);
      if (!chosen) {
        setSelectedSessionId("");
        setSelectedMonth("");
        return true;
      }

      setSelectedSessionId(String(chosen.session_id));
      const month = pickMonthInSession(chosen, resolveMonthForSession(chosen));
      setSelectedMonth(month);
      return true;
    } catch (e) {
      if (reqId !== sessionReqRef.current) return false;
      setMSessionList([]);
      myToast.notify_warning(apiErrorMessage(e, "Network error loading sessions."));
      return false;
    }
  }, []);

  const fetchPayrollMonth = useCallback(async () => {
    if (!selectedMonth || !selectedSessionId) return;
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setPayrollRows([]);
      return;
    }

    const parsed = parseYearMonthKey(selectedMonth);
    if (!parsed) {
      setPayrollRows([]);
      myToast.notify_warning("Invalid month selected.");
      return;
    }

    const reqId = ++payrollReqRef.current;
    setPayrollLoading(true);
    try {
      const response = await axios.get(`${API_BASE()}getPayRollMonthlySalary.php`, {
        params: {
          school_code: schoolCode,
          session_id: selectedSessionId,
          month: parsed.month,
          year: parsed.year,
        },
      });
      if (reqId !== payrollReqRef.current) return;
      if (response.data?.api_status === "OK") {
        setPayrollRows(
          Array.isArray(response.data.staff_payroll) ? response.data.staff_payroll : []
        );
      } else {
        setPayrollRows([]);
        myToast.notify_warning(
          response.data?.api_remark || "Could not load payroll for this month."
        );
      }
    } catch (e) {
      if (reqId !== payrollReqRef.current) return;
      setPayrollRows([]);
      myToast.notify_warning(apiErrorMessage(e, "Network error loading payroll."));
    } finally {
      if (reqId === payrollReqRef.current) setPayrollLoading(false);
    }
  }, [selectedMonth, selectedSessionId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBootLoading(true);
      await Promise.all([loadStaffDirectory(), fetchSessionList()]);
      if (!cancelled) setBootLoading(false);
    })();
    return () => {
      cancelled = true;
      sessionReqRef.current = -1;
      payrollReqRef.current = -1;
    };
  }, [loadStaffDirectory, fetchSessionList]);

  useEffect(() => {
    if (!selectedSessionId || monthOptions.length === 0) return;
    const valid = monthOptions.some((m) => m.value === selectedMonth);
    if (!valid) setSelectedMonth(monthOptions[0].value);
  }, [selectedSessionId, monthOptions, selectedMonth]);

  useEffect(() => {
    if (!selectedMonth || !selectedSessionId) return;
    fetchPayrollMonth();
  }, [selectedMonth, selectedSessionId, fetchPayrollMonth]);

  const handleSessionChange = useCallback(
    (e) => {
      const sess = mSessionList.find((s) => String(s.session_id) === e.target.value);
      if (!sess) return;
      setSelectedSessionId(String(sess.session_id));
      setSelectedMonth(
        pickMonthInSession(sess, resolveMonthForSession(sess) || selectedMonth)
      );
    },
    [mSessionList, selectedMonth]
  );

  const handleRefresh = useCallback(async () => {
    await Promise.all([loadStaffDirectory(), fetchPayrollMonth()]);
  }, [loadStaffDirectory, fetchPayrollMonth]);

  if (!loggedIn) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <h2>Access Denied</h2>
            <p>You must be logged in to view this page.</p>
            <Link to="/Login" className="btn btn-primary">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (bootLoading) return <MyLoader />;

  return (
    <div
      className="content payroll-page sm-page smhub-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Staff Management</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell smhub-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaUsers />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Staff Management</h1>
              <p className="sm-hero__sub">
                {staffDirectory.length} staff
                {periodLabel ? ` · Overview for ${periodLabel}` : ""}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <label className="smhub-select">
              <span className="smhub-select__label">Session</span>
              <select
                value={selectedSessionId}
                onChange={handleSessionChange}
                aria-label="Academic session"
              >
                {mSessionList.length === 0 ? (
                  <option value="">Loading sessions…</option>
                ) : (
                  mSessionList.map((s) => (
                    <option key={s.session_id} value={s.session_id}>
                      {s.session_name}
                      {s.status ? ` (${s.status})` : ""}
                    </option>
                  ))
                )}
              </select>
            </label>
            <label className="smhub-select">
              <span className="smhub-select__label">Month</span>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                disabled={monthOptions.length === 0}
                aria-label="Month"
              >
                {monthOptions.length > 0 ? (
                  monthOptions.map((m) => (
                    <option key={m.key} value={m.value}>
                      {m.label}
                    </option>
                  ))
                ) : (
                  <option value="">No months</option>
                )}
              </select>
            </label>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={payrollLoading || !selectedSessionId}
              className={`pp-icon-btn${payrollLoading ? " spinning" : ""}`}
              title="Refresh data"
              aria-label="Refresh data"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link to="/AdminMain/StaffRegistration" className="smhub-btn-new">
              <span className="smhub-btn-new__icon" aria-hidden>
                <FaUserPlus />
              </span>
              <span className="smhub-btn-new__label">Add Staff</span>
            </Link>
          </div>
        </header>

        <div className="smhub-body">
          <section className="smhub-metrics" aria-label="Key metrics">
            {metrics.map((m) => (
              <KpiChip
                key={m.label}
                label={m.label}
                value={
                  payrollLoading && m.label !== "School Staff" ? "…" : m.value
                }
                icon={m.icon}
                tone={m.tone}
                loading={payrollLoading && m.label !== "School Staff"}
              />
            ))}
          </section>

          <section className="smhub-panel" aria-label="Monthly summary">
            <div className="smhub-panel__head">
              <span className="smhub-panel__step" aria-hidden>
                01
              </span>
              <div className="min-w-0">
                <h2 className="smhub-panel__title">Month snapshot</h2>
                <p className="smhub-panel__sub">
                  Attendance & payroll
                  {periodLabel ? ` for ${periodLabel}` : ""}
                </p>
              </div>
            </div>
            <div className="sm-dashboard-row">
              <div className="pp-summary-card">
                <div className="pp-summary-card__head pp-summary-card__head--inline">
                  <span className="pp-summary-card__icon" aria-hidden>
                    <FaUserClock />
                  </span>
                  <div className="pp-summary-card__head-text min-w-0">
                    <h3 className="pp-summary-card__title">Attendance totals</h3>
                  </div>
                </div>
                <div className="pp-summary-card__body">
                  <SummaryCells cells={attendanceCells} />
                </div>
              </div>

              <div className="pp-summary-card pp-summary-card--status">
                <div className="pp-summary-card__head pp-summary-card__head--inline">
                  <span className="pp-summary-card__icon" aria-hidden>
                    <FaMoneyBillWave />
                  </span>
                  <div className="pp-summary-card__head-text min-w-0">
                    <h3 className="pp-summary-card__title">Payroll (month)</h3>
                  </div>
                  <div className="pp-summary-card__head-metric">
                    <span className="pp-summary-card__head-metric-label">
                      Net
                    </span>
                    <span className="pp-summary-card__head-metric-value">
                      {payrollLoading ? "…" : formatINR(dashboard.monthTotal)}
                    </span>
                  </div>
                </div>
                <div className="pp-summary-card__body">
                  <SummaryCells cells={payrollCells} />
                </div>
              </div>
            </div>
          </section>

          <section
            className="sm-quick-links smhub-quick-links"
            aria-labelledby="sm-quick-links-heading"
          >
            <div className="smhub-panel__head smhub-panel__head--inline">
              <span className="smhub-panel__step" aria-hidden>
                02
              </span>
              <div className="min-w-0">
                <h2
                  id="sm-quick-links-heading"
                  className="smhub-panel__title"
                >
                  Quick links
                </h2>
                <p className="smhub-panel__sub">
                  Attendance, leave, departments & more
                </p>
              </div>
            </div>
            <div className="sm-quick-links__grid">
              {QUICK_LINKS.map((link) => (
                <DashboardTile key={link.to} {...link} />
              ))}
            </div>
          </section>

          <section
            className="smhub-panel smhub-panel--directory"
            aria-label="Staff directory"
          >
            <div className="smhub-panel__head">
              <span className="smhub-panel__step" aria-hidden>
                03
              </span>
              <div className="min-w-0">
                <h2 className="smhub-panel__title">Staff directory</h2>
                <p className="smhub-panel__sub">
                  {filteredStaff.length === staffDirectory.length
                    ? `${staffDirectory.length} member${
                        staffDirectory.length === 1 ? "" : "s"
                      }`
                    : `${filteredStaff.length} of ${staffDirectory.length}`}
                  {biometricMissingCount > 0
                    ? ` · ${biometricMissingCount} without biometric`
                    : ""}
                </p>
              </div>
            </div>

            <div className="smhub-toolbar">
              <div className="smhub-search">
                <FaSearch className="smhub-search__icon" aria-hidden />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search name, role, department, biometric…"
                  aria-label="Search staff"
                />
                {searchQuery ? (
                  <button
                    type="button"
                    className="smhub-search__clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <FaTimes aria-hidden />
                  </button>
                ) : null}
              </div>
              {departmentOptions.length > 0 ? (
                <div
                  className="smhub-dept-chips"
                  role="group"
                  aria-label="Filter by department"
                >
                  <button
                    type="button"
                    className={`smhub-dept-chip${
                      deptFilter === ALL_DEPT ? " is-active" : ""
                    }`}
                    onClick={() => setDeptFilter(ALL_DEPT)}
                  >
                    All
                    <em>{staffDirectory.length}</em>
                  </button>
                  {departmentOptions.map((d) => (
                    <button
                      key={d.name}
                      type="button"
                      className={`smhub-dept-chip${
                        deptFilter === d.name ? " is-active" : ""
                      }`}
                      onClick={() => setDeptFilter(d.name)}
                    >
                      {d.name}
                      <em>{d.count}</em>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="sm-staff-grid">
              {staffDirectory.length === 0 ? (
                <div className="sm-empty smhub-empty">
                  <p className="sm-empty__title">No staff records yet</p>
                  <p className="sm-empty__hint">
                    Register your first staff member to see them listed here.
                  </p>
                  <Link
                    to="/AdminMain/StaffRegistration"
                    className="smhub-btn-new smhub-btn-new--solid"
                  >
                    <span className="smhub-btn-new__icon" aria-hidden>
                      <FaUserPlus />
                    </span>
                    <span className="smhub-btn-new__label">Register staff</span>
                  </Link>
                </div>
              ) : filteredStaff.length === 0 ? (
                <div className="sm-empty smhub-empty">
                  <p className="sm-empty__title">No matches</p>
                  <p className="sm-empty__hint">
                    Try another search or clear the department filter.
                  </p>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSearchQuery("");
                      setDeptFilter(ALL_DEPT);
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                filteredStaff.map((staff, idx) => (
                  <StaffCard
                    key={staff.staff_id ?? staff.id ?? `staff-${idx}`}
                    staff={staff}
                  />
                ))
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default StaffManagement;
